import React from 'react'
import {MapTypeContext} from 'common/Map/MapTypeContext'
import _ from 'lodash'

// Higher-order component to subscribe to map zoom level.
// Works a lot like the context API provided by react-redux.
export const ZoomLevelConsumer = ({children: render}) => (
  <MapTypeContext.Consumer>
    {({mapType, mapInstance}) => {
      switch (mapType) {
        case 'leaflet':
          return <LeafletZoomSubscriber mapInstance={mapInstance} render={render} />
        case 'google':
          return <GoogleZoomSubscriber mapInstance={mapInstance} render={render} />
        case 'mapbox':
          return <MapboxZoomSubscriber mapInstance={mapInstance} render={render} />
      }
    }}
  </MapTypeContext.Consumer>
)

class LeafletZoomSubscriber extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      zoomLevel: props.mapInstance.leafletElement.getZoom()
    }
  }

  componentDidMount () {
    const {mapInstance} = this.props
    mapInstance.leafletElement.on('zoomanim', this.onZoomChange)
    mapInstance.leafletElement.on('zoomend', this.onZoomChange)
  }

  componentWillUnmount () {
    const {mapInstance} = this.props
    mapInstance.leafletElement.off('zoomanim', this.onZoomChange)
    mapInstance.leafletElement.off('zoomend', this.onZoomChange)
  }

  onZoomChange = (e) => {
    const {mapInstance} = this.props
    this.setState({zoomLevel: mapInstance.leafletElement.getZoom(), nextZoomLevel: e.zoom})
  }

  render () {
    const {render} = this.props
    const {zoomLevel, nextZoomLevel} = this.state
    return render({zoomLevel, nextZoomLevel})
  }
}

// Note: with the current legacy js_maps route rendering implementation,
// the map will initialize at a default zoom level and then the zoom_changed
// event will fire when the route is rendered. Because the default zoom level
// is zoomed in fairly close, this can result in unnecessary computation
// being performed before the route bounding box zoom is adopted.
//
// I expect this issue to be resolved when we stop using js_maps and begin
// initializing the gmap with a bounding box the same way we do with Leaflet
class GoogleZoomSubscriber extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      zoomLevel: (props.mapInstance.gmap || props.mapInstance).getZoom()
    }
  }

  gmap = () => {
    const {mapInstance} = this.props
    return mapInstance.gmap || mapInstance
  }

  componentDidMount () {
    this._zoomListener = this.gmap().addListener('zoom_changed', this.onZoomChange)
  }

  componentWillUnmount () {
    this._zoomListener.remove()
  }

  onZoomChange = (e) => {
    this.setState({zoomLevel: this.gmap().getZoom()})
  }

  render () {
    const {render} = this.props
    const {zoomLevel} = this.state
    return render({zoomLevel})
  }
}

class MapboxZoomSubscriber extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      zoomLevel: props.mapInstance.getZoom() + 1 // offset by 1 to account for 512px tiles
    }
  }

  componentDidMount () {
    const {mapInstance} = this.props
    mapInstance.on('zoom', this.zoomChange)
  }

  componentWillUnmount () {
    const {mapInstance} = this.props
    mapInstance.off('zoom', this.zoomChange)
  }

  zoomChange = _.throttle(() => {
    const {mapInstance} = this.props
    this.setState({
      zoomLevel: mapInstance.getZoom() + 1 // offset by 1 to account for 512px tiles
    })
  }, 200)

  render () {
    const {render} = this.props
    const {zoomLevel} = this.state
    return render({zoomLevel})
  }
}
